import React from 'react';
import {
  DeleteAllButton,
  DeleteAllDataContentWrapper,
  DeleteAllText,
  DeleteAllTextWarning,
  Description,
  DownloadButton,
  DownloadDialogContent,
  DownloadPeriodsWrapper,
  DownloadRows,
  DownloadText,
  LeftSidebarContainer,
  PeriodContainer,
  PeriodLabel,
  Title,
  WarningIcon,
  Wrapper,
} from './Data.styles';
import {
  getPersonnel,
  getThirdParty,
  personnelExport,
  thirdPartyExport,
} from 'utils/api';
import {
  IExportDataResponse,
  IFileData,
  IFileDataType,
  IFileStatus,
  IFileUnknownError,
} from 'utils/types';
import { FileTypeSideBarItem } from './FileTypeSideBarItem';
import ConfirmationDialog from 'components/ConfirmDialog/ConfirmDialog';
import { ApPeriodPicker } from '@alixpartners/ui-components';
import { getFileTypeStatus, getSettingsDataLink } from '../settings.utils';
import ConfirmDownloadDialog from 'components/ConfirmDialog/ConfirmDownloadDialog';

type IExportPeriod = {
  year: number;
  period: number;
};

interface IDataState {
  errorData: {
    [IFileDataType.REVENUE]: {};
    [IFileDataType.THIRD_PARTY_SPEND]: {};
    [IFileDataType.PERSONNEL_SPEND]: {};
    [IFileDataType.APPLICATIONS]: {};
  };
  files: IFileData[];
  fileUploadInProgress: boolean;
  isConfirmDisabled: boolean;
  isDeleteAllOpen: boolean;
  isExportThirdPartyOpen: boolean;
  isExportPersonnelOpen: boolean;
  isSuccessNotification: boolean;
  exportData: IExportDataResponse;
  loading: boolean;
  loadingText: string;
  serverError?: IFileUnknownError;
}

const EMPTY_ERROR_DATA = {
  [IFileDataType.REVENUE]: {},
  [IFileDataType.THIRD_PARTY_SPEND]: {},
  [IFileDataType.PERSONNEL_SPEND]: {},
  [IFileDataType.APPLICATIONS]: {},
};

interface IDataViewProps {
  fileDataType: IFileDataType;
  files: IFileData[];
  isUploadDisabled: boolean;
  loading: boolean;
  isDownloadDisabled: boolean;
  isDeleteDisabled: boolean;
  deleteAllFiles: () => void;
}

const getPeriodDataFromStr = (s?: string) => {
  if (!s) {
    return undefined;
  }
  const dateArr = s.split('-');
  return {
    year: Number(dateArr[0] || ''),
    period: Number(dateArr[1] || ''),
  };
};

const getDateString = (period: IExportPeriod) =>
  `${period.year}-${
    period.period > 9 ? period.period : `0${period.period}`
  }-01T00:00:00`;

export class LeftSideBar extends React.Component<IDataViewProps, IDataState> {
  state = {
    errorData: EMPTY_ERROR_DATA,
    exportData: {
      startDate: '',
      endDate: '',
      rowsInTotal: 0,
    },
    files: [],
    fileUploadInProgress: false,
    isConfirmDisabled: false,
    isSuccessNotification: false,
    loading: true,
    loadingText: '',
    isDeleteAllOpen: false,
    isExportThirdPartyOpen: false,
    isExportPersonnelOpen: false,
    serverError: undefined,
  };

  openDeleteAllDialog = () => {
    this.setState({ isDeleteAllOpen: true });
  };

  closeDeleteAllDialog = () => {
    this.setState({ isDeleteAllOpen: false });
  };

  openThirdPartyDialog = () => {
    this.setState({ isExportThirdPartyOpen: true });
  };

  closeThirdPartyDialog = () => {
    this.setState({ isExportThirdPartyOpen: false });
  };

  openPersonnelDialog = () => {
    this.setState({ isExportPersonnelOpen: true });
  };

  closePersonnelDialog = () => {
    this.setState({ isExportPersonnelOpen: false });
  };

  closeAllExport = () => {
    this.setState({
      isExportPersonnelOpen: false,
      isExportThirdPartyOpen: false,
    });
  };

  getFileAbout = (params: { startDate: string; endDate: string }) => {
    const { exportData, isExportPersonnelOpen } = this.state;
    (isExportPersonnelOpen ? getPersonnel : getThirdParty)(params).then(
      (resp: IExportDataResponse) => {
        this.setState({
          exportData: {
            ...exportData,
            rowsInTotal: resp.rowsInTotal,
          },
        });
      }
    );
  };

  render() {
    const {
      exportData,
      isDeleteAllOpen,
      isExportThirdPartyOpen,
      isExportPersonnelOpen,
      isConfirmDisabled,
    } = this.state;
    const {
      fileDataType,
      files,
      isUploadDisabled,
      isDownloadDisabled,
      isDeleteDisabled,
      deleteAllFiles,
    } = this.props;
    const revenueFiles = files.filter(
      (f: IFileData) => f.fileDataType === IFileDataType.REVENUE
    );
    const thirdPartyFiles: IFileData[] = files.filter(
      (f: IFileData) => f.fileDataType === IFileDataType.THIRD_PARTY_SPEND
    );
    const personnelFiles: IFileData[] = files.filter(
      (f: IFileData) => f.fileDataType === IFileDataType.PERSONNEL_SPEND
    );
    const applicationFiles: IFileData[] = files.filter(
      (f: IFileData) => f.fileDataType === IFileDataType.APPLICATIONS
    );
    const productsFiles: IFileData[] = files.filter(
      (f: IFileData) => f.fileDataType === IFileDataType.PRODUCTS
    );
    const revenueFileStatus = getFileTypeStatus(revenueFiles);
    const thirdPartyFilesStatus = getFileTypeStatus(thirdPartyFiles);
    const personnelFilesStatus = getFileTypeStatus(personnelFiles);
    const applicationFilesStatus = getFileTypeStatus(applicationFiles);
    const productFilesStatus = getFileTypeStatus(productsFiles);

    const thirdPartyPublished = thirdPartyFiles.some(
      (file) => file.fileState === IFileStatus.PUBLISHED
    );
    const personnelPublished = personnelFiles.some(
      (file) => file.fileState === IFileStatus.PUBLISHED
    );
    return (
      <LeftSidebarContainer>
        <Wrapper>
          {!isUploadDisabled && (
            <>
              <Title>Upload</Title>
              <Description>
                <p>Data upload process:</p>
                <p>1. Upload revenue data</p>
                <p>2. Upload at least one spend file</p>
                <p>3. Classify new data</p>
                <p>4. Publish the classified data</p>
              </Description>
            </>
          )}
          <FileTypeSideBarItem
            link={getSettingsDataLink('revenue')}
            isActive={fileDataType === IFileDataType.REVENUE}
            status={revenueFileStatus}
            text="Revenue"
          />
          <FileTypeSideBarItem
            link={getSettingsDataLink('third-party-spend')}
            isActive={fileDataType === IFileDataType.THIRD_PARTY_SPEND}
            status={thirdPartyFilesStatus}
            text="Third Party spend"
          />
          <FileTypeSideBarItem
            link={getSettingsDataLink('personnel-spend')}
            isActive={fileDataType === IFileDataType.PERSONNEL_SPEND}
            status={personnelFilesStatus}
            text="Personnel spend"
          />
          <FileTypeSideBarItem
            link={getSettingsDataLink('applications')}
            isActive={fileDataType === IFileDataType.APPLICATIONS}
            status={applicationFilesStatus}
            text="Applications (optional)"
            application
          />
          <FileTypeSideBarItem
            link={getSettingsDataLink('products')}
            isActive={fileDataType === IFileDataType.PRODUCTS}
            status={productFilesStatus}
            text="Products (optional)"
            application
          />
        </Wrapper>
        {(thirdPartyPublished || personnelPublished) && !isDownloadDisabled ? (
          <>
            <Wrapper>
              <Title>Download</Title>
              <Description>
                You can download classified and published data
              </Description>
              {thirdPartyPublished && (
                <DownloadButton
                  iconName="save_alt"
                  onClick={() => {
                    getThirdParty().then((exportData: IExportDataResponse) => {
                      this.setState({
                        isExportThirdPartyOpen: true,
                        exportData,
                      });
                    });
                  }}
                >
                  download third party data
                </DownloadButton>
              )}
              {personnelPublished && (
                <DownloadButton
                  iconName="save_alt"
                  onClick={() => {
                    getPersonnel().then((exportData: IExportDataResponse) => {
                      this.setState({
                        isExportPersonnelOpen: true,
                        exportData,
                      });
                    });
                  }}
                >
                  download personnel data
                </DownloadButton>
              )}
            </Wrapper>
          </>
        ) : null}
        {!isDeleteDisabled && (
          <Wrapper>
            <Title>Delete</Title>
            <Description>
              All data deletion does not affect vendor classification rules
            </Description>
            <DeleteAllButton
              iconName="delete"
              onClick={this.openDeleteAllDialog}
            >
              Delete all data
            </DeleteAllButton>
          </Wrapper>
        )}
        <ConfirmationDialog
          confirmText="delete all data"
          isOpen={isDeleteAllOpen}
          title="Delete all data"
          message={
            <DeleteAllDataContentWrapper>
              <WarningIcon iconName="outline_warning" iconSize="88px" />
              <DeleteAllText>
                Are you sure you want to delete all data from the application?
              </DeleteAllText>
              <DeleteAllTextWarning>
                This action cannot be undone
              </DeleteAllTextWarning>
            </DeleteAllDataContentWrapper>
          }
          onClose={this.closeDeleteAllDialog}
          onConfirm={deleteAllFiles}
        />
        <ConfirmDownloadDialog
          confirmText="generate .xls file"
          isOpen={isExportThirdPartyOpen || isExportPersonnelOpen}
          title={`Download ${
            isExportThirdPartyOpen ? 'Third party' : 'Personnel'
          } data`}
          message={
            <DownloadDialogContent>
              <DownloadText>
                You can download the full data set or choose a smaller time
                range by selecting custom time period.
              </DownloadText>
              {exportData ? (
                <DownloadPeriodsWrapper>
                  <PeriodContainer>
                    <PeriodLabel>From</PeriodLabel>
                    <ApPeriodPicker
                      data={getPeriodDataFromStr(exportData.startDate)}
                      enableBeforeToday
                      isQuarter={false}
                      onChange={(p: IExportPeriod) => {
                        const params = {
                          startDate: getDateString(p),
                          endDate: exportData.endDate,
                        };
                        this.setState({
                          exportData: {
                            ...exportData,
                            startDate: params.startDate,
                          },
                        });
                        this.getFileAbout(params);
                      }}
                      showPeriod={false}
                    />
                  </PeriodContainer>
                  <PeriodContainer>
                    <PeriodLabel>To</PeriodLabel>
                    <ApPeriodPicker
                      data={getPeriodDataFromStr(exportData.endDate)}
                      enableBeforeToday
                      isQuarter={false}
                      onChange={(p: IExportPeriod) => {
                        const params = {
                          startDate: exportData.startDate,
                          endDate: getDateString(p),
                        };
                        this.setState({
                          exportData: {
                            ...exportData,
                            endDate: params.endDate,
                          },
                        });
                        this.getFileAbout(params);
                      }}
                      showPeriod={false}
                    />
                  </PeriodContainer>
                </DownloadPeriodsWrapper>
              ) : null}
              <DownloadRows>
                {exportData.rowsInTotal} rows in total
              </DownloadRows>
            </DownloadDialogContent>
          }
          onClose={this.closeAllExport}
          isConfirmDisabled={isConfirmDisabled}
          onConfirm={() => {
            (isExportThirdPartyOpen ? thirdPartyExport : personnelExport)(
              exportData
            ).then(() => {
              this.setState({ isConfirmDisabled: false });
              this.closeAllExport();
            });
            this.setState({
              isConfirmDisabled: true,
              isSuccessNotification: true,
            });
            setTimeout(() => {
              this.setState({ isSuccessNotification: false });
            }, 5000);
          }}
        />
      </LeftSidebarContainer>
    );
  }
}
